import { useTranslations } from 'next-intl'

import type { QueryDslQueryContainer } from '@elastic/elasticsearch/lib/api/types'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Lozenge from '~/components/atoms/Lozenge/Lozenge'
import SlideOutPanel from '~/components/atoms/SlideOutPanel/SlideOutPanel'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type {
  ArtworksListProps,
  filtersType,
  formatsType,
} from '~/components/blocks/ArtworksList/ArtworksList'
import { useSettingsContext } from '~/contexts/settingsContext'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import type { FetchedSettings } from '~/services/sanity/siteSettings'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

import type { TSiteArtists } from '~~/scripts/siteArtistsToJson'

import AllArtworksCatalogue from '../AllArtworksCatalogue/AllArtworksCatalogue'
import Catalogue from '../Catalogue/Catalogue'

import FilterList from './FilterList'
import FilterRange from './FilterRange'
import useHooks from './hooks'

import styles from './Filters.module.css'

export interface FiltersProps {
  filters: filtersType
  genres: FetchedSettings['genres'] | undefined
  formats: formatsType | undefined
  artists?: TSiteArtists['allArtists'] | undefined
  prices?: any | undefined
  viewAll?: boolean
  searchTerm?: string
  clearSearchTerm?: () => void
  filterQueries?: QueryDslQueryContainer[]
  totals?: {
    total: number
    totalTitle: string
  }
  testID?: string
  searchInsightsEvents?: {
    filter?: TSearchAnalystEventProps
    catalogue?: TSearchAnalystEventProps
  }
  hideFilters?: ArtworksListProps['hideFilters']
  hideTradingFloor?: boolean
  pushRight?: boolean
  noMarginleft?: boolean
  initialFilters?: Partial<filtersType>
}

export default function Filters({
  filters,
  genres,
  artists,
  formats,
  prices,
  viewAll = false,
  searchTerm,
  clearSearchTerm,
  filterQueries,
  totals,
  testID,
  searchInsightsEvents,
  hideFilters,
  hideTradingFloor,
  pushRight,
  noMarginleft = false,
  initialFilters,
}: FiltersProps) {
  const t = useTranslations('common')
  const tNavigation = useTranslations('navigation')
  const {
    openFilter,
    isSlideOutOpen,
    closeFilters,
    clearAllFilters,
    updatePricesHandler,
    getTitle,
    activeFilterType,
    updateGenresHandler,
    checkedGenres,
    clearGenreFilters,
    updateFormatsHandler,
    checkedFormats,
    clearFormatFilters,
    selectedPrices,
    handleTradingFilter,
    checkedArtists,
    updateArtistHandler,
    clearArtistFilters,
  } = useHooks(
    artists,
    genres,
    prices,
    formats,
    filters,
    clearSearchTerm,
    initialFilters
  )
  const { allArtists } = useSettingsContext()

  return (
    <>
      <div
        className={classNames(
          styles.filterLozengesWrap,
          pushRight && styles.filterLozengesPushRight,
          noMarginleft && styles.noMarginleft
        )}
        data-testid={testID}
      >
        <div className={styles.filterLozenges}>
          {artists &&
            artists.length > 1 &&
            !hideFilters?.includes('artist') && (
              <Lozenge
                hideOnZero
                isActive={filters.artists.length > 0}
                count={filters.artists.length > 0 ? 'x' : undefined}
                title={t('artist')}
                testAttr="artist-filter-lozenge"
                onClick={async () => {
                  openFilter('artist')
                  await multiPostHogEventPusher([
                    {
                      eventType: 'Artworks: Artists filter clicked',
                    },
                  ])
                  // const isArtistFilterActive =
                  //   checkedArtists.filter((x: any) => x.checked === true)
                  //     .length > 0

                  // if (!isArtistFilterActive) openFilter('artist')
                  // if (isArtistFilterActive) clearArtistFilters()
                }}
                iconClick={e => {
                  e.stopPropagation()
                  clearArtistFilters()
                }}
                testID={testID && `lozenge-artist-${testID}`}
              />
            )}
          {prices && prices.length > 1 && (
            <Lozenge
              hideOnZero
              isActive={filters.prices.length > 0}
              count={filters.prices.length > 0 ? 'x' : undefined}
              title={t('price')}
              testAttr="price-filter-lozenge"
              onClick={async () => {
                openFilter('listPriceMin')
                await multiPostHogEventPusher([
                  {
                    eventType: 'Artworks: Price filter clicked',
                  },
                ])
              }}
              iconClick={e => {
                e.stopPropagation()
                updatePricesHandler([] as any)
              }}
              testID={testID && `lozenge-price-${testID}`}
            />
          )}
          {!hideTradingFloor && (
            <>
              <Lozenge
                title={t('forSale')}
                hideOnZero
                onClick={async () => {
                  handleTradingFilter('sell')
                  await multiPostHogEventPusher([
                    {
                      eventType: 'Artworks: For sale filter clicked',
                    },
                  ])
                }}
                isActive={filters.tradingFloor === 'sell'}
                count={filters.tradingFloor === 'sell' ? 'x' : undefined}
              />
              <Lozenge
                title={t('wanted')}
                hideOnZero
                onClick={async () => {
                  handleTradingFilter('buy')
                  await multiPostHogEventPusher([
                    {
                      eventType: 'Artworks: Wanted filter clicked',
                    },
                  ])
                }}
                isActive={filters.tradingFloor === 'buy'}
                count={filters.tradingFloor === 'buy' ? 'x' : undefined}
              />
              <Lozenge
                title={t('inCollections')}
                hideOnZero
                onClick={async () => {
                  handleTradingFilter('owned')
                  await multiPostHogEventPusher([
                    {
                      eventType: 'Artworks: In network filter clicked',
                    },
                  ])
                }}
                isActive={filters.tradingFloor === 'owned'}
                count={filters.tradingFloor === 'owned' ? 'x' : undefined}
              />
            </>
          )}

          {genres && genres.length >= 1 && !hideFilters?.includes('genre') && (
            <Lozenge
              title={tNavigation('artworksTab.genres')}
              count={filters.genres?.length}
              hideOnZero={true}
              isActive={filters.genres.length > 0}
              testAttr="genre-filter-lozenge"
              onClick={async () => {
                openFilter('genre')

                await multiPostHogEventPusher([
                  {
                    eventType: 'Artworks: Genre filter clicked',
                  },
                ])
              }}
              testID={testID && `lozenge-genre-${testID}`}
            />
          )}
          {formats &&
            formats.length >= 1 &&
            !hideFilters?.includes('format') && (
              <Lozenge
                title={t('format')}
                count={filters.formats?.length}
                hideOnZero={true}
                onClick={async () => {
                  openFilter('format')

                  await multiPostHogEventPusher([
                    {
                      eventType: 'Artworks: Formats filter clicked',
                    },
                  ])
                }}
                testID={testID && `lozenge-format-${testID}`}
              />
            )}

          {viewAll && (
            <div className={styles.rightButtons}>
              <Lozenge
                title={`${t('viewAZ')} ${
                  totals && totals.total > 0 ? `(${totals.total})` : ''
                }`}
                hideOnZero={true}
                onClick={async () => {
                  openFilter('viewAll')
                  await multiPostHogEventPusher([
                    {
                      eventType: 'Artworks: View all filter clicked',
                    },
                  ])
                }}
                testID={testID && `lozenge-viewAll-${testID}`}
                className={styles.viewAll}
              />
            </div>
          )}

          {(filters.artists?.length > 0 ||
            filters.genres?.length > 0 ||
            filters.formats?.length > 0 ||
            filters.prices?.length > 0 ||
            (searchTerm && searchTerm.length > 0)) && (
            <button
              onClick={clearAllFilters}
              className={styles.clearAllBtn}
              data-testid={testID && `clear-all-${testID}`}
            >
              <Text size={TextSizes.secondaryTitleMicro}>{t('clearAll')}</Text>
            </button>
          )}

          {/* <TradingToggle updateUrl={updateUrl} /> */}
        </div>
      </div>

      <SlideOutPanel
        isShowing={isSlideOutOpen}
        closeHandler={closeFilters}
        animateChildren={false}
        testID={testID && `genre-slideout-${testID}`}
        title={getTitle()}
        testAttr="filter-slideout-panel"
      >
        {activeFilterType === 'artist' && (
          <>
            {artists && (
              <div data-test="artist-catalogue-filter">
                <Catalogue
                  testAttrItems="artist-catalogue-filter-profile"
                  items={
                    [
                      ...artists.map(artist => {
                        const artistItem = allArtists?.find(
                          x => x.slug === artist.slug
                        )
                        return {
                          ...artist,
                          image: artistItem?.image,
                        }
                      }),
                    ] as any
                  }
                  multipleActive={[...checkedArtists]}
                  customClickHandler={async (id: string) => {
                    updateArtistHandler(id)
                    await multiPostHogEventPusher([
                      {
                        eventType: 'Artworks: Filtered by artist',
                      },
                    ])
                  }}
                  key={`artist-catalogue-filter`}
                  testID={testID && `filterlist-artist-${testID}`}
                />
              </div>
            )}
            {filters.artists?.length > 0 && (
              <div className={styles.wrapper}>
                <div className={styles.filterListClearButton}>
                  <Button
                    variant={ButtonVariants.secondaryDark}
                    button={{
                      type: ButtonTypes.button,
                      onClick: clearArtistFilters,
                    }}
                    testID={testID && `filterlist-clear-genre-${testID}`}
                  >
                    {t('clearArtists')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        <div className={styles.wrapper}>
          {activeFilterType === 'genre' && (
            <div data-test="genre-filter-dialog">
              <FilterList
                items={genres}
                // title="Genre"
                changeHandler={updateGenresHandler}
                checkedItems={checkedGenres}
                testID={testID && `filterlist-genre-${testID}`}
                searchInsightsEvent={searchInsightsEvents?.filter}
                filterType={activeFilterType}
                testAttr="genre-filter-profile"
              />

              {filters.genres?.length > 0 && (
                <div className={styles.filterListClearButton}>
                  <Button
                    variant={ButtonVariants.secondaryDark}
                    button={{
                      type: ButtonTypes.button,
                      onClick: clearGenreFilters,
                    }}
                    testID={testID && `filterlist-clear-genre-${testID}`}
                  >
                    {t('clearGenres')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeFilterType === 'format' && (
            <>
              <FilterList
                items={formats}
                // title="Format"
                changeHandler={updateFormatsHandler}
                checkedItems={checkedFormats}
                testID={testID && `filterlist-format-${testID}`}
                searchInsightsEvent={searchInsightsEvents?.filter}
                filterType={activeFilterType}
              />
              {filters.formats?.length > 0 && (
                <div className={styles.filterListClearButton}>
                  <Button
                    variant={ButtonVariants.secondaryDark}
                    button={{
                      type: ButtonTypes.button,
                      onClick: clearFormatFilters,
                    }}
                    testID={testID && `filterlist-clear-format-${testID}`}
                  >
                    {t('clearFormats')}
                  </Button>
                </div>
              )}
            </>
          )}

          {activeFilterType === 'listPriceMin' && (
            <div data-test="price-filter-dialog">
              <FilterRange
                changeHandler={updatePricesHandler}
                selectedPrices={selectedPrices}
                prices={prices}
                closeFilters={closeFilters}
                filterType={activeFilterType}
                testAttr="price-filter-range"
              />
            </div>
          )}
          {activeFilterType === 'viewAll' && (
            //wrap the catalogue in a new component to call the use search hook on render
            <AllArtworksCatalogue
              total={totals?.total || 0}
              filterQueries={filterQueries}
              searchInsightsEvent={searchInsightsEvents?.catalogue}
            />
          )}
        </div>
      </SlideOutPanel>
    </>
  )
}
